import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { withTurkishGenitive } from '../turkishSuffixes';
import MiniTaskGameHeader from './MiniTaskGameHeader';
import useMiniTaskCountdown from '../useMiniTaskCountdown';
import NumericAnswerInput, { numericAnswerLength } from './NumericAnswerInput';
import MathVisual, { supportsMathVisual } from './MathVisual';
import ComparisonAnswerInput, { comparisonQuestion } from './ComparisonAnswerInput';
import NeighborTensAnswer, { neighborTensQuestion } from './NeighborTensAnswer';
import DecompositionAnswerInput, { decompositionFields } from './DecompositionAnswerInput';
import NumberSequenceAnswer, { numberSequence } from './NumberSequenceAnswer';
import { CHOICE_TONES, CHOICE_FEEDBACK } from '../choicePalette';
import MiniTaskDialogue, { isMiniTaskDialogue } from './MiniTaskDialogue';
import ListeningControl from '../listening/ListeningControl';
import useListeningPlayback from '../listening/useListeningPlayback';
import { STAFF_LISTENING_CONTRACT } from '../listening/listeningApi';

const skillLabels = {
  ...Object.fromEntries([4, 5, 6, 7, 8].map((grade) => [`basic_religious_knowledge_${grade}`, 'Temel Dinî Bilgiler'])),
  ...Object.fromEntries([2, 3, 4, 5, 6, 7, 8].map((grade) => [`curriculum_math_${grade}`, 'Matematik Yolculuğu'])),
  ...Object.fromEntries([2, 3, 4, 5, 6, 7, 8].map((grade) => [`curriculum_turkish_${grade}`, 'Haftanın Türkçesi'])),
  addition: 'Toplama Görevi',
  subtraction: 'Çıkarma Görevi',
  multiplication: 'Çarpma Görevi',
  division: 'Bölme Görevi',
  mixed_arithmetic: 'Karışık İşlemler Görevi',
  word_meaning_relations: 'Eş Anlam, Zıt Anlam, Eşses',
  punctuation_marks: 'Noktalama İşaretleri',
  proverbs_idioms: 'Atasözleri ve Deyimler',
  bilgi_avi: 'Bilgi-Avı',
  dunyayi_tani: 'Dünyayı Tanı',
  turkiyeyi_tani: 'Ülkemizi Tanıyalım',
  vocabulary: 'Vocabulary',
};

const flagImageOutline =
  'drop-shadow(1px 0 0 rgba(15,23,42,0.28)) drop-shadow(-1px 0 0 rgba(15,23,42,0.28)) drop-shadow(0 1px 0 rgba(15,23,42,0.28)) drop-shadow(0 -1px 0 rgba(15,23,42,0.28))';

function isFlagMedia(media) {
  const url = String(media?.url || '');
  return /(?:^|\/)flag_[^/?#]+\.png(?:[?#].*)?$/i.test(url);
}

function FeedbackPanel({ feedback, correctAnswer, panelRef, isEnglish = false, numericEntry = false }) {
  if (!feedback?.message) {
    return (
      <Box
        ref={panelRef}
        sx={{
          minHeight: { xs: 76, md: 104 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#37306f',
          fontWeight: 900,
          fontSize: { xs: 16, md: 20 },
          gap: 1,
          '@media (max-height: 820px) and (min-width: 600px)': { minHeight: 72 },
        }}
      >
        <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: '#a78bfa', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 -3px 0 rgba(15,23,42,0.12)' }}>♥</Box>
        {numericEntry ? 'Cevabını yaz ve kontrol et.' : isEnglish ? 'Choose your answer.' : 'Cevabını seç ve devam et.'}
      </Box>
    );
  }

  const isCorrect = feedback.type === 'correct';
  return (
    <Box
      ref={panelRef}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      sx={{
        minHeight: { xs: 86, md: 112 },
        borderRadius: 3,
        border: `2px solid ${isCorrect ? '#86efac' : '#fdba74'}`,
        bgcolor: isCorrect ? '#f0fdf4' : '#fff7ed',
        px: { xs: 1.5, md: 3 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 1.2, md: 2 },
        boxShadow: '0 8px 18px rgba(15,23,42,0.08)',
        '@media (max-height: 820px) and (min-width: 600px)': {
          minHeight: 84,
        },
      }}
    >
      <StarRoundedIcon sx={{ color: '#facc15', fontSize: { xs: 50, md: 68 }, filter: 'drop-shadow(0 4px 0 #f59e0b)', '@media (max-height: 820px) and (min-width: 600px)': { fontSize: 52 } }} />
      <Box>
        <Typography sx={{ color: isCorrect ? '#15803d' : '#c2410c', fontWeight: 1000, fontSize: { xs: 26, md: 42 }, lineHeight: 1.08, '@media (max-height: 820px) and (min-width: 600px)': { fontSize: 34 } }}>
          {isEnglish
            ? (isCorrect ? 'Great job!' : 'Almost!')
            : (isCorrect ? 'Harika, doğru!' : 'Neredeyse!')}
        </Typography>
        <Typography sx={{ color: '#6d5d93', fontWeight: 900, fontSize: { xs: 17, md: 25 }, lineHeight: 1.15, mt: 0.3, '@media (max-height: 820px) and (min-width: 600px)': { fontSize: 21 } }}>
          {isEnglish
            ? (isCorrect ? 'Here comes the next question...' : `The correct answer is ${correctAnswer}.`)
            : (isCorrect ? 'Sıradaki soru geliyor...' : `Doğru cevap ${correctAnswer}.`)}
        </Typography>
      </Box>
    </Box>
  );
}

function SafeImage({ src, alt, sx, outlined = false }) {
  if (!src) return null;
  return (
    <Box
      component="img"
      src={src}
      alt={alt || ''}
      data-flag-outline={outlined ? 'true' : undefined}
      loading="eager"
      onError={(event) => {
        event.currentTarget.style.visibility = 'hidden';
      }}
      sx={{
        display: 'block',
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        filter: outlined ? flagImageOutline : undefined,
        ...sx,
      }}
    />
  );
}

function FloatingCloud({ sx }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        width: { xs: 58, md: 88 },
        height: { xs: 22, md: 32 },
        borderRadius: 99,
        bgcolor: 'rgba(255,255,255,0.9)',
        filter: 'drop-shadow(0 4px 7px rgba(76,29,149,0.08))',
        '&::before, &::after': { content: '""', position: 'absolute', bgcolor: 'inherit', borderRadius: '50%' },
        '&::before': { width: '42%', height: '110%', left: '15%', top: '-45%' },
        '&::after': { width: '50%', height: '135%', right: '9%', top: '-62%' },
        ...sx,
      }}
    />
  );
}

function Flower({ color = '#fb7185', sx }) {
  return (
    <Box sx={{ position: 'absolute', width: 54, height: 64, ...sx }}>
      <Box sx={{ position: 'absolute', left: 26, top: 28, width: 4, height: 34, borderRadius: 99, bgcolor: '#2f8f32' }} />
      <Box sx={{ position: 'absolute', left: 12, top: 28, width: 22, height: 10, borderRadius: '50%', bgcolor: '#5dbb44', transform: 'rotate(-28deg)' }} />
      <Box sx={{ position: 'absolute', right: 8, top: 38, width: 22, height: 10, borderRadius: '50%', bgcolor: '#7ac943', transform: 'rotate(24deg)' }} />
      {[0, 72, 144, 216, 288].map((deg) => (
        <Box
          key={deg}
          sx={{
            position: 'absolute',
            left: 18,
            top: 7,
            width: 20,
            height: 24,
            borderRadius: '60% 60% 50% 50%',
            bgcolor: color,
            transform: `rotate(${deg}deg) translateY(-9px)`,
            transformOrigin: '50% 22px',
            boxShadow: 'inset 0 -3px 0 rgba(15,23,42,0.08)',
          }}
        />
      ))}
      <Box sx={{ position: 'absolute', left: 21, top: 17, width: 14, height: 14, borderRadius: '50%', bgcolor: '#facc15' }} />
    </Box>
  );
}

function LeafSprig({ sx }) {
  return (
    <Box sx={{ position: 'absolute', width: 86, height: 110, ...sx }}>
      <Box sx={{ position: 'absolute', left: 38, bottom: 6, width: 5, height: 96, borderRadius: 99, bgcolor: '#2f8f32', transform: 'rotate(19deg)' }} />
      {[0, 1, 2, 3].map((idx) => (
        <React.Fragment key={idx}>
          <Box sx={{ position: 'absolute', left: 22 + idx * 3, bottom: 24 + idx * 18, width: 34, height: 18, borderRadius: '60% 10% 60% 10%', bgcolor: '#65a30d', transform: 'rotate(-28deg)' }} />
          <Box sx={{ position: 'absolute', right: 6 + idx * 5, bottom: 32 + idx * 17, width: 32, height: 17, borderRadius: '10% 60% 10% 60%', bgcolor: '#84cc16', transform: 'rotate(26deg)' }} />
        </React.Fragment>
      ))}
    </Box>
  );
}

function BushCluster({ side = 'left' }) {
  const isLeft = side === 'left';
  return (
    <Box
      sx={{
        position: 'absolute',
        left: isLeft ? { xs: -34, md: -18 } : 'auto',
        right: isLeft ? 'auto' : { xs: -34, md: -18 },
        bottom: { xs: -18, md: -12 },
        width: { xs: 260, md: 370 },
        height: { xs: 188, md: 260 },
        transform: isLeft ? 'none' : 'scaleX(-1)',
      }}
    >
      <Box sx={{ position: 'absolute', left: 0, bottom: 0, width: '62%', height: '58%', borderRadius: '55% 55% 0 0', bgcolor: '#4aa83a', boxShadow: 'inset 18px 18px 0 rgba(132,204,22,0.55)' }} />
      <Box sx={{ position: 'absolute', left: '18%', bottom: 16, width: '45%', height: '50%', borderRadius: '55% 55% 0 0', bgcolor: '#77bd32', boxShadow: 'inset -15px 14px 0 rgba(255,255,255,0.08)' }} />
      <Box sx={{ position: 'absolute', left: '41%', bottom: 0, width: '44%', height: '42%', borderRadius: '55% 55% 0 0', bgcolor: '#8cc63e' }} />
      <LeafSprig sx={{ left: { xs: 10, md: 18 }, bottom: { xs: 54, md: 84 } }} />
      <Flower color="#fb7185" sx={{ left: { xs: 78, md: 112 }, bottom: { xs: 34, md: 52 }, transform: { xs: 'scale(0.8)', md: 'scale(1)' } }} />
      <Flower color="#ffffff" sx={{ left: { xs: 150, md: 222 }, bottom: { xs: 66, md: 88 }, transform: { xs: 'scale(0.64)', md: 'scale(0.78)' } }} />
      <Box sx={{ position: 'absolute', left: { xs: 38, md: 60 }, bottom: { xs: 82, md: 118 }, width: 12, height: 12, borderRadius: '50%', bgcolor: '#facc15', boxShadow: '17px 11px 0 #fff, 26px -8px 0 #fff, 38px 6px 0 #fff' }} />
      <Box sx={{ position: 'absolute', left: { xs: 160, md: 260 }, bottom: { xs: 24, md: 26 }, width: 28, height: 10, borderRadius: '50%', bgcolor: '#65a30d', transform: 'rotate(-20deg)' }} />
    </Box>
  );
}

function FlyingAccent() {
  return (
    <Box sx={{ position: 'absolute', right: { xs: 14, md: 82 }, bottom: { xs: 190, md: 250 }, width: 120, height: 70, display: { xs: 'none', sm: 'block' } }}>
      <Box sx={{ position: 'absolute', left: 36, top: 22, width: 27, height: 18, borderRadius: '50%', bgcolor: '#facc15', boxShadow: 'inset -8px 0 0 #111827, inset -15px 0 0 #facc15, inset -20px 0 0 #111827' }} />
      <Box sx={{ position: 'absolute', left: 54, top: 16, width: 17, height: 14, borderRadius: '50%', border: '2px solid #bfdbfe', bgcolor: 'rgba(219,234,254,0.6)', transform: 'rotate(28deg)' }} />
      <Box sx={{ position: 'absolute', left: 42, top: 11, width: 17, height: 14, borderRadius: '50%', border: '2px solid #bfdbfe', bgcolor: 'rgba(219,234,254,0.6)', transform: 'rotate(-18deg)' }} />
      <Box sx={{ position: 'absolute', left: 28, top: 28, width: 5, height: 5, borderRadius: '50%', bgcolor: '#111827' }} />
      <Box sx={{ position: 'absolute', left: 68, top: 29, width: 48, height: 28, borderTop: '2px dashed #c4b5fd', borderRadius: '50%', transform: 'rotate(-16deg)' }} />
    </Box>
  );
}

export function GardenBackdrop() {
  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      <FloatingCloud sx={{ left: { xs: -12, md: 8 }, top: { xs: 260, md: 290 } }} />
      <FloatingCloud sx={{ right: { xs: -18, md: 20 }, top: { xs: 300, md: 304 } }} />
      <Box sx={{ position: 'absolute', left: '-6%', right: '-6%', bottom: -92, height: { xs: 160, md: 210 }, borderRadius: '50% 50% 0 0', bgcolor: '#a7d84b', transform: 'rotate(-1.5deg)', boxShadow: 'inset 0 20px 0 rgba(255,255,255,0.14)' }} />
      <Box sx={{ position: 'absolute', left: '62%', right: '-12%', bottom: -76, height: { xs: 155, md: 205 }, borderRadius: '50% 50% 0 0', bgcolor: '#8bc83f', transform: 'rotate(-8deg)' }} />
      <Box sx={{ position: 'absolute', left: '-18%', width: '48%', bottom: -82, height: { xs: 146, md: 202 }, borderRadius: '50% 50% 0 0', bgcolor: '#94cf46', transform: 'rotate(8deg)' }} />
      <BushCluster side="left" />
      <BushCluster side="right" />
      <FlyingAccent />
      <Box sx={{ position: 'absolute', left: { xs: 12, md: 84 }, top: { xs: 410, md: 455 }, color: '#c084fc', fontSize: { xs: 22, md: 28 }, fontWeight: 1000 }}>✿</Box>
      <Box sx={{ position: 'absolute', right: { xs: 20, md: 120 }, top: { xs: 475, md: 510 }, color: '#fb7185', fontSize: { xs: 24, md: 32 }, fontWeight: 1000 }}>✿</Box>
      <Box sx={{ position: 'absolute', right: { xs: 50, md: 168 }, bottom: { xs: 175, md: 205 }, color: '#93c5fd', fontSize: { xs: 20, md: 26 }, fontWeight: 1000 }}>✦</Box>
      <Box sx={{ position: 'absolute', left: { xs: 28, md: 265 }, top: { xs: 320, md: 356 }, color: '#facc15', fontSize: { xs: 20, md: 25 }, fontWeight: 1000 }}>✦</Box>
    </Box>
  );
}

export default function RoundView({
  round,
  question,
  feedback,
  submittingChoice,
  selectedChoice,
  correctAnswer,
  classTitle,
  soundEnabled,
  onToggleSound,
  onBackToBoard,
  onAnswer,
  boardSessionId,
  boardToken,
  staffSchoolId,
  onTimeout,
  timerActive = true,
  timedOut = false,
  timeoutSaving = false,
  timeoutError = '',
  assetUrls = {},
  qaMode = false,
  answerRevealed = false,
  hideFeedbackPanel = false,
  answerPending = false,
  answerError = '',
}) {
  const listeningRound = Boolean(round?.listening_contract || question?.listening);
  const staffListening = Boolean(staffSchoolId && question?.listening?.contract === STAFF_LISTENING_CONTRACT);
  const alreadyScored = listeningRound && typeof question?.is_correct === 'boolean';
  const listeningPlayback = useListeningPlayback({ roundId: round?.round_id, question, boardSessionId, boardToken,
    staffSchoolId: staffListening ? staffSchoolId : undefined,
    disabled: alreadyScored || answerPending || Boolean(submittingChoice) || Boolean(feedback?.message) || timedOut });
  const listeningAnswerBlocked = listeningRound && (alreadyScored || (!staffListening && !round?.listening_qa_scoring)
    || (question?.listening && listeningPlayback.status !== 'ended'));
  const isUntimedPreview = Boolean(round?.preview && round?.duration_seconds == null);
  const remainingSeconds = useMiniTaskCountdown({
    roundId: round?.round_id,
    boardSessionId,
    durationSeconds: round?.duration_seconds,
    persist: false,
    active: Boolean(question && !timedOut && timerActive && !isUntimedPreview && !listeningRound),
    onExpire: onTimeout,
  });
  const choices = question?.choices || [];
  const numericLength = numericAnswerLength(round, question);
  const decomposition = decompositionFields(question);
  const sequenceTokens = numberSequence(question);
  const questionPosition = Number(question?.position || 1);
  const questionCount = Number(round?.question_count || 5);
  const hasFeedback = Boolean(feedback?.message);
  const shouldRevealAnswer = hasFeedback || answerRevealed;
  const isTurkish = round?.lesson?.code === 'TURKCE';
  const isEnglish = round?.lesson?.code === 'ENGLISH';
  const isDkab = round?.lesson?.code === 'DKAB';
  const isEnglishJourney = isEnglish && round?.skill_key === 'curriculum_english_3';
  const isMathCurriculum = /^curriculum_math_[2-8]$/.test(round?.skill_key || '');
  const comparison = isMathCurriculum ? comparisonQuestion(question) : null;
  const neighborTens = isMathCurriculum ? neighborTensQuestion(question) : null;
  const isTurkishCurriculum = /^curriculum_turkish_[2-8]$/.test(round?.skill_key || '');
  const mathSequenceMatch = isMathCurriculum
    ? /^(Boşluğu tamamla:|Örüntüyü tamamla:)\s*(.+)$/.exec(String(question?.prompt || ''))
    : null;
  const mathSequence = mathSequenceMatch?.[2];
  const useTextTypography = isTurkish || isEnglish || isDkab || isMathCurriculum;
  const isGeneralKnowledge = round?.lesson?.code === 'GENEL_KULTUR';
  const skillLabel = isMathCurriculum && round?.adaptive_review?.is_review
    ? 'Matematik Tekrar'
    : isTurkishCurriculum && round?.adaptive_review?.is_review
      ? 'Türkçe Tekrarı'
    : isEnglishJourney
      ? (round?.adaptive_review?.is_review ? 'İngilizce Tekrarı' : 'İngilizce Yolculuğu')
    : skillLabels[round?.skill_key]
    || (isEnglish ? `${round?.lesson?.name || 'Mini'} Task` : `${round?.lesson?.name || 'Mini'} Görevi`);
  const studentName = round?.student?.display_name || (isEnglish ? 'Student' : 'Öğrenci');
  const isTextQuestion = useTextTypography || isGeneralKnowledge;
  const questionMedia = question?.media || null;
  // Place-value lookup tables disclose the digit asked for in these questions.
  const omitPlaceValueChart = isMathCurriculum && question?.visual_spec?.renderer === 'place_value_chart';
  const hasMathVisual = isMathCurriculum && !omitPlaceValueChart && supportsMathVisual(question?.visual_spec);
  const isAnalogClock = hasMathVisual && question?.visual_spec?.renderer === 'analog_clock';
  const isShapeSequence = hasMathVisual && question?.visual_spec?.renderer === 'shape_sequence';
  const shapeSequencePrompt = isShapeSequence
    ? /^([^:]+:)/.exec(String(question?.prompt || ''))?.[1]
    : null;
  const hasMissingMathVisual = isMathCurriculum && !omitPlaceValueChart && round?.preview && Boolean(question?.visual_spec) && !hasMathVisual;
  const choiceMedia = question?.listening ? listeningPlayback.choiceMedia : question?.choice_media || {};
  // Dialogue occupies the existing question-media slot, never a second visual.
  // Functional math diagrams always take precedence when both are supplied.
  const hasDialogue = isMiniTaskDialogue(question?.dialogue) && !hasMathVisual && !hasMissingMathVisual;
  const hasQuestionMedia = hasDialogue || hasMathVisual || hasMissingMathVisual || Boolean(questionMedia?.url);
  const hasChoiceMedia = Object.keys(choiceMedia || {}).length > 0;
  const hasVisualMedia = hasQuestionMedia || hasChoiceMedia;
  const hideChoiceText = (hasChoiceMedia || Boolean(question?.listening)) && question?.option_display_mode === 'images';
  const largeVisualChoiceColumnCount = Math.max(1, Math.min(choices.length, 4));
  const visualChoiceGridColumns = hasChoiceMedia
    ? {
        xs: 'repeat(2, minmax(0, 1fr))',
        sm: choices.length === 3 ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
        md: choices.length === 3 ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
        lg: `repeat(${largeVisualChoiceColumnCount}, minmax(0, 1fr))`,
      }
    : hasQuestionMedia
      ? {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: choices.length === 3 ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
        }
      : { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' };
  const visualContentMaxWidth = hasChoiceMedia ? 1480 : hasQuestionMedia ? 1320 : 1260;
  const feedbackPanelRef = useRef(null);

  useEffect(() => {
    if (!hasFeedback) return;
    feedbackPanelRef.current?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [hasFeedback]);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        bgcolor: '#fffaf0',
        color: '#172554',
        overflowX: 'hidden',
        backgroundImage:
          'radial-gradient(circle at 8% 36%, rgba(255,255,255,0.9) 0 54px, transparent 55px), radial-gradient(circle at 92% 34%, rgba(255,255,255,0.82) 0 52px, transparent 53px), linear-gradient(180deg, #fffaf0 0%, #fff8ea 70%, #f7f4d6 100%)',
      }}
    >
      {round?.preview && (
        <Box sx={{ position: 'fixed', bottom: 12, left: 12, zIndex: 20, bgcolor: '#facc15', color: '#713f12', px: 1.5, py: 0.5, borderRadius: 99, fontWeight: 1000, boxShadow: '0 3px 0 rgba(146,64,14,0.16)' }}>
          {qaMode ? 'İÇERİK QA' : (isEnglish ? 'PREVIEW' : 'TEST MODU')}
        </Box>
      )}
      <GardenBackdrop />
      <Box sx={{ position: 'relative', zIndex: 1, mx: 'auto', width: '100%', maxWidth: 1720, boxSizing: 'border-box', px: { xs: 1.2, sm: 2, md: 3.2 }, pb: { xs: 2, md: 3 } }}>
        <MiniTaskGameHeader isEndless={Boolean(round?.is_endless)} classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} language={isEnglish ? 'en' : 'tr'} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: '220px 1fr 220px' },
            alignItems: 'center',
            gap: { xs: 1.2, md: 2 },
            mt: { xs: 2, md: 4 },
            mb: { xs: 3.2, sm: 1.5, md: 2.4 },
            ...(hasDialogue ? {
              '@media (min-width: 900px) and (max-height: 1100px)': { mt: 2, mb: 1.2 },
            } : {}),
            '@media (max-height: 820px) and (min-width: 600px)': {
              mt: 2,
              mb: 1,
            },
          }}
        >
          <ButtonBase
            onClick={onBackToBoard}
            sx={{
              justifySelf: { xs: 'start', md: 'center' },
              gridColumn: { xs: '1', md: 'auto' },
              gridRow: { xs: '1', md: 'auto' },
              borderRadius: 3,
              border: '2px solid #d8b4fe',
              bgcolor: 'rgba(255,255,255,0.86)',
              color: '#33258a',
              px: { xs: 1.5, md: 2.2 },
              py: { xs: 0.9, md: 1.2 },
              minWidth: { xs: 146, md: 210 },
              minHeight: { xs: 52, md: 60 },
              '&:focus-visible, &.Mui-focusVisible': { outline: '3px solid #4c1d95', outlineOffset: '3px' },
              boxShadow: '0 4px 0 rgba(91,33,182,0.10)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: { xs: 25, md: 31 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 25 }, lineHeight: 1 }}>{isEnglish ? 'Home' : 'Ana sayfa'}</Typography>
          </ButtonBase>

          <Typography
            sx={{
              textAlign: 'center',
              gridColumn: { xs: '1 / -1', md: 'auto' },
              gridRow: { xs: '2', md: 'auto' },
              color: '#251777',
              fontWeight: 1000,
              fontSize: { xs: 28, sm: 42, md: 54 },
              lineHeight: 1,
              overflowWrap: 'anywhere',
              textShadow: '0 3px 0 rgba(255,255,255,0.94)',
            }}
          >
            {isEnglish ? `${studentName}'s Mini Task` : `${withTurkishGenitive(studentName)} Mini Görevi`}
          </Typography>

          <Box
            sx={{
              justifySelf: { xs: 'end', md: 'center' },
              gridColumn: { xs: '2', md: 'auto' },
              gridRow: { xs: '1', md: 'auto' },
              borderRadius: 3,
              border: '2px solid #d8b4fe',
              bgcolor: 'rgba(255,255,255,0.86)',
              color: '#33258a',
              px: { xs: 1.5, md: 2.2 },
              py: { xs: 0.9, md: 1.2 },
              minWidth: { xs: 118, md: 210 },
              textAlign: 'center',
              boxShadow: '0 4px 0 rgba(91,33,182,0.10)',
            }}
          >
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 25 }, lineHeight: 1 }}>
              {isEnglish ? `Question ${questionPosition} / ${questionCount}` : `Soru ${questionPosition} / ${questionCount}`}
            </Typography>
            <Typography role="timer" sx={{ mt: 0.45, fontWeight: 900, fontSize: { xs: 14, md: 18 } }}>
              {isUntimedPreview ? (isEnglish ? 'No time limit' : 'Süre sınırı yok') : listeningRound ? 'Round not started · 120 s' : `${isEnglish ? 'Time' : 'Süre'}: ${remainingSeconds} sn`}
            </Typography>
          </Box>
        </Box>

        {timedOut && (
          <Box role="alert" sx={{ maxWidth: 720, mx: 'auto', mb: 2, p: 2, textAlign: 'center', borderRadius: 3, bgcolor: '#fff7ed', color: '#9a3412', fontWeight: 900 }}>
            {timeoutSaving ? 'Süre doldu, sonucun kaydediliyor...' : timeoutError || 'Süre doldu.'}
            {timeoutError && <Button onClick={onTimeout} sx={{ ml: 1, fontWeight: 900 }}>Tekrar dene</Button>}
          </Box>
        )}

        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: 'calc(100vw - 20px)', sm: visualContentMaxWidth },
            boxSizing: 'border-box',
            mx: 'auto',
            position: 'relative',
            borderRadius: 4,
            bgcolor: 'rgba(255,255,255,0.94)',
            border: '2px solid #e9d5ff',
            boxShadow: '0 15px 35px rgba(76,29,149,0.14), inset 0 0 0 2px rgba(255,255,255,0.84)',
            px: hasVisualMedia ? { xs: 1.2, sm: 2, md: 3.2, lg: 4.2 } : { xs: 1.4, sm: 2.4, md: 5 },
            pt: hasVisualMedia ? { xs: 3.9, md: 4.8 } : { xs: 4.5, md: 5.6 },
            pb: hasVisualMedia ? { xs: 1.1, md: 1.6 } : { xs: 1.6, md: 2.4 },
            ...(hasDialogue ? {
              '@media (min-width: 900px) and (max-height: 1100px)': { pt: 3.1, pb: 1.1 },
            } : {}),
            '@media (max-height: 820px) and (min-width: 600px)': {
              pt: hasVisualMedia ? 3.4 : 4,
              pb: 1.4,
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: 0,
              transform: 'translate(-50%, -50%)',
              borderRadius: 2.5,
              border: '2px solid #d8b4fe',
              bgcolor: '#f5f0ff',
              color: '#6d45df',
              px: { xs: 1.4, md: 2.2 },
              py: { xs: 0.55, md: 0.75 },
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              minWidth: { xs: 190, md: 270 },
              justifyContent: 'center',
              boxShadow: '0 4px 0 rgba(91,33,182,0.10)',
            }}
          >
            <StarRoundedIcon sx={{ color: '#facc15', fontSize: { xs: 25, md: 31 }, filter: 'drop-shadow(0 2px 0 #f59e0b)' }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 23 }, lineHeight: 1 }}>{skillLabel}</Typography>
          </Box>

          {isTurkishCurriculum && question?.stimulus && !question?.audio_required && (
            <Box sx={{ position: 'relative', zIndex: 1, mx: 'auto', my: 2, p: 2, maxWidth: 900, borderRadius: 3, bgcolor: '#f5f3ff' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap', textAlign: 'left', fontSize: { xs: 18, md: 26 }, lineHeight: 1.5 }}>
                {question.stimulus}
              </Typography>
            </Box>
          )}
          {isTurkishCurriculum && (round?.preview || qaMode) && question?.missing_assets?.length > 0 && (
            <Typography role="status" sx={{ position: 'relative', zIndex: 1, color: '#9a3412', fontWeight: 800, textAlign: 'center', my: 2 }}>
              Bu soru öğrenci gösterimine hazır değil. Eksik: {question.missing_assets.join(', ')}.
            </Typography>
          )}
          {isTurkishCurriculum && qaMode && question?.assessment_mode === 'proxy' && (
            <Typography sx={{ color: '#475569', textAlign: 'center', mb: 1 }}>
              Dolaylı ölçüm: ilgili becerinin doğrudan uygulaması değerlendirilmez.
            </Typography>
          )}
          <Box sx={{ position: 'absolute', zIndex: 0, left: { xs: 18, md: 72 }, top: { xs: 54, md: 58 }, color: '#a78bfa', fontSize: 26, fontWeight: 1000 }}>✦</Box>
          <Box sx={{ position: 'absolute', right: { xs: 18, md: 72 }, top: { xs: 64, md: 70 }, color: '#93c5fd', fontSize: 20, fontWeight: 1000 }}>✦</Box>
          <Box sx={{ position: 'absolute', right: { xs: 34, md: 56 }, top: '42%', color: '#fb7185', fontSize: 28, fontWeight: 1000 }}>✦</Box>

          {!comparison && !hasDialogue && <Typography
            sx={{
              color: '#191756',
              position: 'relative',
              zIndex: 1,
              fontWeight: 1000,
              fontSize: useTextTypography
                ? isMathCurriculum ? { xs: 25, sm: 32, md: 43, lg: 48 } : { xs: isDkab ? 28 : 32, sm: 42, md: 58 }
                : isGeneralKnowledge
                  ? hasVisualMedia
                    ? { xs: 25, sm: 33, md: 43, lg: 50, xl: 56 }
                    : { xs: 40, sm: 54, md: 76, lg: 84 }
                  : { xs: 58, sm: 78, md: 118 },
              lineHeight: isDkab ? 1.25 : isTextQuestion ? 1.08 : 1,
              textAlign: 'center',
              letterSpacing: 0,
              overflowWrap: isDkab ? 'normal' : 'anywhere',
              wordBreak: isDkab ? 'normal' : undefined,
              textShadow: hasVisualMedia || isDkab ? '0 3px 0 rgba(25,23,86,0.08)' : '0 5px 0 rgba(25,23,86,0.10)',
              mb: hasVisualMedia ? { xs: 1.2, md: 1.5 } : { xs: 2.4, md: 3.2 },
              '@media (max-height: 820px) and (min-width: 600px)': {
                fontSize: useTextTypography ? (isMathCurriculum ? 38 : 46) : isGeneralKnowledge ? (hasVisualMedia ? 38 : 62) : 88,
                mb: hasVisualMedia ? 1 : 1.8,
              },
              '@media (max-height: 980px) and (min-width: 900px)': {
                fontSize: useTextTypography ? (isMathCurriculum ? 42 : 50) : isGeneralKnowledge ? (hasVisualMedia ? 42 : 68) : 94,
                mb: hasVisualMedia ? 1.1 : 2,
              },
              ...(hasDialogue ? {
                '@media (min-width: 900px) and (max-height: 1100px)': { fontSize: 41, mb: 1 },
              } : {}),
            }}
          >
            {mathSequence ? <>
              <Box component="span" sx={{ display: 'block' }}>{mathSequenceMatch[1]}</Box>
              <Box component="span" sx={{
                display: 'block',
                mt: { xs: 0.5, md: 0.8 },
                fontSize: mathSequence.length >= 23 ? { xs: 18, sm: 27, md: 40 } : 'inherit',
                whiteSpace: 'nowrap',
                overflowWrap: 'normal',
              }}>{mathSequence}</Box>
            </> : shapeSequencePrompt || question?.prompt}
          </Typography>}

          {question?.listening && <ListeningControl playback={listeningPlayback}
            previewScoring={staffListening ? !qaMode : Boolean(round?.listening_qa_scoring)} staffReview={staffListening}
            scored={alreadyScored}
            disabled={alreadyScored || answerPending || Boolean(submittingChoice) || hasFeedback || timedOut} />}
          {answerError && <Typography role="alert" sx={{ color: '#37306f', textAlign: 'center', mb: 1 }}>{answerError}</Typography>}
          {hasQuestionMedia && (hasDialogue ? (
            <Box data-testid="mini-task-question-media" sx={{ position: 'relative', zIndex: 1, mx: 'auto', mb: { xs: 1, md: 1.1 }, width: '100%', maxWidth: 1120 }}>
              <MiniTaskDialogue dialogue={question.dialogue} className="mini-task-dialogue--fit-viewport" />
            </Box>
          ) : (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                mx: 'auto',
                mb: { xs: 1, md: 1.1 },
                width: isAnalogClock ? { xs: 'min(65vw, 250px)', sm: 280, md: 300 } : hasMathVisual || hasMissingMathVisual ? { xs: '100%', sm: 'min(100%, 520px)', md: 600 } : { xs: 'min(70vw, 250px)', sm: 300, md: 360, lg: 400, xl: 440 },
                aspectRatio: isAnalogClock ? '1 / 1' : hasMathVisual || hasMissingMathVisual ? '600 / 280' : '1 / 1',
                borderRadius: 3,
                bgcolor: '#ffffff',
                border: '2px solid #fed7aa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 0.45, md: 0.6 },
                boxShadow: '0 6px 0 rgba(251,146,60,0.10), 0 12px 22px rgba(15,23,42,0.07)',
                overflow: 'hidden',
                '@media (max-height: 820px) and (min-width: 600px)': {
                  width: isAnalogClock ? 220 : 320,
                },
              }}
            >
              {neighborTens ? <NeighborTensAnswer
                question={neighborTens} selectedChoice={selectedChoice} correctAnswer={correctAnswer}
                reveal={shouldRevealAnswer} disabled={qaMode || Boolean(submittingChoice) || hasFeedback || timedOut || (!isUntimedPreview && remainingSeconds === 0)}
                onAnswer={onAnswer}
              /> : hasMathVisual ? <MathVisual spec={question.visual_spec} /> : hasMissingMathVisual ? (
                <Typography role="status" sx={{ p: 2, textAlign: 'center', color: '#9a3412', fontWeight: 800, fontSize: { xs: 16, md: 22 } }}>
                  {question.visual_requirement === 'Opsiyonel' ? 'İsteğe bağlı çizim' : 'Soru çizimi'} henüz hazır değil: {question.visual_spec.renderer}
                </Typography>
              ) : (
                <SafeImage
                  src={assetUrls[questionMedia.url] || questionMedia.url}
                  alt={questionMedia.alt_text || (isEnglish ? 'Question image' : 'Soru görseli')}
                  outlined={isFlagMedia(questionMedia)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 2.4,
                    objectFit: 'cover',
                  }}
                />
              )}
            </Box>
          ))}

          {round?.preview && isMathCurriculum && question?.visual_spec && question?.visual_ready === false && hasMathVisual && (
            <Typography role="status" sx={{ color: '#9a3412', fontWeight: 800, textAlign: 'center', mb: 1 }}>
              Bu çizim öğrenci gösterimine henüz hazır değil.
            </Typography>
          )}

          {neighborTens ? null : sequenceTokens ? <NumberSequenceAnswer
            key={`${round?.round_id}:${question?.id}`}
            questionKey={`${round?.round_id}:${question?.id}`} tokens={sequenceTokens}
            disabled={qaMode || Boolean(submittingChoice) || hasFeedback || timedOut || (!isUntimedPreview && remainingSeconds === 0)}
            correctTokenOrder={qaMode ? question?.qa_correct_token_order : shouldRevealAnswer ? feedback?.correct_token_order : undefined} onAnswer={onAnswer}
          /> : decomposition ? <DecompositionAnswerInput
            key={`${round?.round_id}:${question?.id ?? question?.key ?? question?.position}`}
            fields={decomposition} qaMode={qaMode} reveal={shouldRevealAnswer} correctAnswer={correctAnswer}
            disabled={Boolean(submittingChoice) || hasFeedback || timedOut || (!isUntimedPreview && remainingSeconds === 0)}
            onAnswer={onAnswer}
          /> : comparison ? <ComparisonAnswerInput
            comparison={comparison}
            selectedChoice={selectedChoice}
            correctAnswer={correctAnswer}
            reveal={shouldRevealAnswer}
            disabled={qaMode || Boolean(submittingChoice) || hasFeedback || timedOut || (!isUntimedPreview && remainingSeconds === 0)}
            onAnswer={onAnswer}
          /> : numericLength ? <NumericAnswerInput
            key={`${round?.round_id}:${question?.id ?? question?.key ?? question?.position}`}
            length={numericLength}
            disabled={Boolean(submittingChoice) || hasFeedback || timedOut || (!isUntimedPreview && remainingSeconds === 0)}
            qaMode={qaMode}
            correctAnswer={correctAnswer}
            onAnswer={onAnswer}
          /> : (          <Box
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: visualChoiceGridColumns,
              gap: hasChoiceMedia ? { xs: 1, sm: 1.4, md: 1.8, lg: 2.1 } : { xs: 1.4, md: 2.2 },
              mb: hasChoiceMedia ? { xs: 1.1, md: 1.7 } : hasQuestionMedia ? { xs: 1, md: 1.3 } : { xs: 1.4, md: 2.2 },
              '@media (max-height: 820px) and (min-width: 600px)': {
                gap: hasChoiceMedia ? 1.1 : 1.3,
                mb: hasChoiceMedia ? 1 : 1.3,
              },
            }}
          >
            {choices.map((choice, idx) => {
              const tone = CHOICE_TONES[idx % CHOICE_TONES.length];
              const mediaForChoice = choiceMedia?.[String(choice)];
              const isSelected = String(selectedChoice) === String(choice);
              const isCorrectChoice = correctAnswer != null && String(correctAnswer) === String(choice);
              const isPatternShapeChoice = isShapeSequence && ['▲', '■', '⬟'].includes(String(choice));
              const isWrongSelected = shouldRevealAnswer && isSelected && !isCorrectChoice;
              const isCorrectSelected = shouldRevealAnswer && isSelected && isCorrectChoice;
              const activeBorder = isCorrectSelected || (shouldRevealAnswer && isCorrectChoice) ? CHOICE_FEEDBACK.correct.border : isWrongSelected ? CHOICE_FEEDBACK.wrong.border : tone.border;
              const activeBg = isCorrectSelected || (shouldRevealAnswer && isCorrectChoice) ? CHOICE_FEEDBACK.correct.bg : isWrongSelected ? CHOICE_FEEDBACK.wrong.bg : tone.bg;
              return (
                <ButtonBase
                  key={choice}
                  aria-label={hideChoiceText ? (isEnglish ? `Choice ${idx + 1}` : `Seçenek ${idx + 1}`) : undefined}
                  onClick={() => onAnswer(choice)}
                  disabled={listeningAnswerBlocked || answerPending || qaMode || Boolean(submittingChoice) || hasFeedback || timedOut || (!isUntimedPreview && remainingSeconds === 0)}
                  sx={{
                    position: 'relative',
                    '&:focus-visible, &.Mui-focusVisible': { outline: '3px solid #4c1d95', outlineOffset: '3px' },
                    gridColumn: hasVisualMedia && choices.length === 3 && idx === 2 ? { xs: '1 / -1', sm: 'auto' } : 'auto',
                    alignSelf: 'stretch',
                    minHeight: hasChoiceMedia
                      ? { xs: 188, sm: 226, md: 262, lg: 288, xl: 316 }
                      : { xs: 76, md: isGeneralKnowledge ? 88 : 112 },
                    borderRadius: hasChoiceMedia ? { xs: 3.2, md: 3.8 } : 3,
                    bgcolor: activeBg,
                    border: `4px solid ${activeBorder}`,
                    boxShadow: isCorrectSelected
                      ? CHOICE_FEEDBACK.correct.shadow
                      : '0 5px 0 rgba(15,23,42,0.08), 0 12px 20px rgba(15,23,42,0.08)',
                    color: isCorrectSelected ? CHOICE_FEEDBACK.correct.text : isPatternShapeChoice ? '#2563eb' : '#191756',
                    fontWeight: 1000,
                    fontSize: useTextTypography
                      ? isPatternShapeChoice ? { xs: 34, sm: 42, md: 50 } : { xs: 22, sm: 27, md: 34 }
                      : isGeneralKnowledge
                        ? hasChoiceMedia
                          ? { xs: 18, sm: 21, md: 25, lg: 28, xl: 30 }
                          : hasQuestionMedia
                            ? { xs: 25, sm: 31, md: 40, lg: 44 }
                          : { xs: 34, sm: 42, md: 48 }
                        : { xs: 38, md: 62 },
                    lineHeight: isTextQuestion ? (hasChoiceMedia ? 1.12 : 1.08) : 1,
                    px: hasChoiceMedia ? { xs: 0.7, sm: 0.9, md: 1.1 } : isTextQuestion ? { xs: 2, md: 3 } : 1,
                    py: hasChoiceMedia ? { xs: 0.8, sm: 1, md: 1.2 } : undefined,
                    overflowWrap: isDkab ? 'normal' : 'anywhere',
                    wordBreak: isDkab ? 'normal' : undefined,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: hasChoiceMedia ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: hideChoiceText ? 'center' : hasChoiceMedia ? 'space-between' : 'center',
                    gap: hideChoiceText ? 0 : hasChoiceMedia ? { xs: 0.55, md: 0.75 } : 0,
                    transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
                    '@media (max-height: 820px) and (min-width: 600px)': {
                      minHeight: hasChoiceMedia ? 252 : isGeneralKnowledge ? 76 : 88,
                    },
                    '@media (max-height: 980px) and (min-width: 900px)': {
                      minHeight: hasChoiceMedia ? 264 : isGeneralKnowledge ? 80 : 96,
                      fontSize: useTextTypography ? 31 : isGeneralKnowledge ? (hasChoiceMedia ? 24 : 43) : 54,
                    },
                    '&:hover': {
                      transform: qaMode || Boolean(submittingChoice) || hasFeedback ? 'none' : 'translateY(-2px)',
                      boxShadow: qaMode || Boolean(submittingChoice) || hasFeedback ? undefined : '0 7px 0 rgba(15,23,42,0.08), 0 16px 26px rgba(79,70,229,0.16)',
                    },
                  }}
                >
                  {isCorrectSelected && <CheckCircleRoundedIcon sx={{ position: 'absolute', left: { xs: 10, md: 16 }, top: { xs: 10, md: 14 }, fontSize: { xs: 30, md: 42 }, color: '#16a34a' }} />}
                  {submittingChoice === choice ? (
                    <CircularProgress size={42} />
                  ) : (
                    <>
                      {mediaForChoice?.url && (
                        <Box
                          sx={{
                            position: 'relative',
                            width: { xs: 130, sm: 164, md: 198, lg: 228, xl: 252 },
                            height: { xs: 130, sm: 164, md: 198, lg: 228, xl: 252 },
                            maxWidth: '100%',
                            borderRadius: 2.6,
                            bgcolor: 'rgba(255,255,255,0.82)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 0.18, md: 0.25 },
                            boxShadow: '0 5px 12px rgba(15,23,42,0.07)',
                            flex: '0 0 auto',
                            mt: { xs: 0, md: 0.1 },
                            overflow: 'hidden',
                            '@media (max-height: 820px) and (min-width: 600px)': {
                              width: 204,
                              height: 204,
                            },
                            '@media (max-height: 980px) and (min-width: 900px)': {
                              width: 210,
                              height: 210,
                            },
                          }}
                        >
                          <SafeImage
                            src={assetUrls[mediaForChoice.url] || mediaForChoice.url}
                            alt={mediaForChoice.alt_text || (isEnglish ? `${choice} image` : `${choice} görseli`)}
                            outlined={isFlagMedia(mediaForChoice)}
                            sx={{
                              width: '100%',
                              height: '100%',
                              borderRadius: 2.2,
                              objectFit: 'cover',
                            }}
                          />
                          {hideChoiceText && mediaForChoice.emoji && (
                            <Box
                              component="span"
                              aria-hidden="true"
                              sx={{
                                position: 'absolute',
                                right: { xs: 7, md: 10 },
                                bottom: { xs: 6, md: 9 },
                                fontSize: { xs: 30, md: 40 },
                                lineHeight: 1,
                                filter: 'drop-shadow(0 2px 2px rgba(15,23,42,0.24))',
                              }}
                            >
                              {mediaForChoice.emoji}
                            </Box>
                          )}
                        </Box>
                      )}
                      {hideChoiceText && !mediaForChoice?.url && <Box component="span">{`Choice ${idx + 1}`}</Box>}
                      {!hideChoiceText && (
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            maxWidth: '100%',
                            minHeight: hasChoiceMedia ? { xs: 42, sm: 46, md: 54, lg: 58 } : 'auto',
                            px: hasChoiceMedia ? { xs: 0.6, md: 1 } : 0,
                            py: hasChoiceMedia ? { xs: 0.45, md: 0.6 } : 0,
                            borderRadius: hasChoiceMedia ? 2.4 : 0,
                            bgcolor: hasChoiceMedia ? 'rgba(255,255,255,0.72)' : 'transparent',
                            boxShadow: hasChoiceMedia ? 'inset 0 0 0 1px rgba(255,255,255,0.84)' : 'none',
                          }}
                        >
                          {choice}
                        </Box>
                      )}
                    </>
                  )}
                </ButtonBase>
              );
            })}
          </Box>) }

          {!hideFeedbackPanel && (!listeningRound || staffListening || round?.listening_qa_scoring) && <FeedbackPanel feedback={feedback} correctAnswer={correctAnswer} panelRef={feedbackPanelRef} isEnglish={isEnglish} numericEntry={Boolean(numericLength || decomposition)} />}
        </Box>
      </Box>
    </Box>
  );
}
